import React, { useState, useEffect, KeyboardEvent, ChangeEvent } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, List, ListItem, TextField, Box, Collapse, Paper, Typography, Chip } from '@mui/material';
import { Add, Delete, ExpandMore, ExpandLess, Edit, Check } from '@mui/icons-material';
import { MuiColorInput } from 'mui-color-input';
import SearchableDropdown from './SearchableDropdown';


type SampleGroupModalProps = {
  open: boolean;
  onClose: () => void;
  onSave: (sampleGroups: Record<string, number[]>, colors: Record<string, string>) => void;
  sampleGroupToSampleDict: Record<string, number[]>;
  sampleGroupOptions: SearchableDropdownOption[];
  tumorDict: Record<number, string>;
  availableSamples: SearchableDropdownOption[];
  initialColors: Record<string, string>;
};

const DEFAULT_COLOR = '#ADD8E6'; // Light blue

const SampleGroupModal: React.FC<SampleGroupModalProps> = ({
  open,
  onClose,
  onSave,
  sampleGroupToSampleDict,
  sampleGroupOptions,
  tumorDict,
  availableSamples,
  initialColors
}) => {
  const [localSampleGroups, setLocalSampleGroups] = useState<Record<string, number[]>>({});
  const [localColors, setLocalColors] = useState<Record<string, string>>({});
  const [groupOrder, setGroupOrder] = useState<string[]>([]);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [editingGroupName, setEditingGroupName] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState<string>('');
  const [unassignedSamples, setUnassignedSamples] = useState<SearchableDropdownOption[]>([]);

  useEffect(() => {
    if (open) {
      const initialGroups: Record<string, number[]> = {};
      const initialLocalColors: Record<string, string> = {};
      const initialOrder: string[] = [];
      
      sampleGroupOptions.forEach(option => {
        const groupName = option.label;
        const groupId = option.id!.toString();
        initialGroups[groupName] = sampleGroupToSampleDict[groupId] || [];
        
        const color = initialColors[groupId];
        initialLocalColors[groupName] = color && color !== 'null' ? color : DEFAULT_COLOR;
        
        initialOrder.push(groupName);
      });
      
      setLocalSampleGroups(initialGroups);
      setLocalColors(initialLocalColors);
      setGroupOrder(initialOrder);
      setExpandedGroups(new Set(initialOrder));
      setEditingGroupName(null);
      updateUnassignedSamples(initialGroups);
    }
  }, [open, sampleGroupToSampleDict, sampleGroupOptions, availableSamples, initialColors]);

  const updateUnassignedSamples = (groups: Record<string, number[]>) => {
    const assignedSamples = new Set(Object.values(groups).flat());
    setUnassignedSamples(availableSamples.filter(sample => !assignedSamples.has(Number(sample.id))));
  };

  const handleStartEditing = (groupName: string) => {
    setEditingGroupName(groupName);
    setEditingValue(groupName);
  };

  const handleGroupNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setEditingValue(e.target.value);
  };

  const handleFinishEditing = (oldName: string, newName: string) => {
    if (oldName !== newName && !localSampleGroups.hasOwnProperty(newName)) {
      setLocalSampleGroups(prev => {
        const { [oldName]: samples, ...rest } = prev;
        return { ...rest, [newName]: samples };
      });
      setLocalColors(prev => {
        const { [oldName]: color, ...rest } = prev;
        return { ...rest, [newName]: color };
      });
      setGroupOrder(prev => prev.map(name => name === oldName ? newName : name));
    }
    setEditingGroupName(null);
  };

  const handleKeyPress = (event: KeyboardEvent<HTMLDivElement>, oldName: string) => {
    if (event.key === 'Enter') {
      handleFinishEditing(oldName, editingValue);
    }
  };

  const generateColorDict = () => {
    const newColorDict: Record<string, string> = {};
    Object.entries(localSampleGroups).forEach(([groupName, samples]) => {
      const groupColor = localColors[groupName] || DEFAULT_COLOR;
      samples.forEach(sampleId => {
        newColorDict[sampleId.toString()] = groupColor;
      });
    });
    return newColorDict;
  };

  const handleGroupAdd = () => {
    const newGroupName = `New Group ${Object.keys(localSampleGroups).length + 1}`;
    setLocalSampleGroups(prev => {
      const newGroups = {
        ...prev,
        [newGroupName]: []
      };
      updateUnassignedSamples(newGroups);
      return newGroups;
    });
    setLocalColors(prev => ({
      ...prev,
      [newGroupName]: DEFAULT_COLOR
    }));
    setGroupOrder(prev => [...prev, newGroupName]);
    setEditingGroupName(newGroupName);
    setEditingValue(newGroupName);
    setExpandedGroups(prev => new Set(prev).add(newGroupName));
  };

  const handleGroupDelete = (groupName: string) => {
    setLocalSampleGroups(prev => {
      const { [groupName]: _, ...newGroups } = prev;
      updateUnassignedSamples(newGroups);
      return newGroups;
    });
    setLocalColors(prev => {
      const { [groupName]: _, ...newColors } = prev;
      return newColors;
    });
    setGroupOrder(prev => prev.filter(name => name !== groupName));
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      newSet.delete(groupName);
      return newSet;
    });
  };

  const handleSampleChange = (groupName: string, newValue: SearchableDropdownOption[]) => {
    const newSampleIds = newValue.map(option => Number(option.id));
    setLocalSampleGroups(prev => {
      const newGroups = {
        ...prev,
        [groupName]: newSampleIds
      };
      updateUnassignedSamples(newGroups);
      return newGroups;
    });
  };

  const handleColorChange = (groupName: string, newColor: string) => {
    setLocalColors(prev => ({
      ...prev,
      [groupName]: newColor
    }));
  };

  const handleGroupToggle = (groupName: string) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupName)) {
        newSet.delete(groupName);
      } else {
        newSet.add(groupName);
      }
      return newSet;
    });
  };

  const getAvailableSamplesForGroup = (currentGroupName: string) => {
    const assignedToOtherGroups = new Set(
      Object.entries(localSampleGroups)
        .filter(([name]) => name !== currentGroupName)
        .flatMap(([, samples]) => samples)
    );
    return [
      ...unassignedSamples,
      ...localSampleGroups[currentGroupName].map(id => ({
        id: id,
        label: tumorDict[id],
        value: id.toString()
      }))
    ].filter(sample => !assignedToOtherGroups.has(Number(sample.id)));
  };

  return (
    <Dialog 
      open={open} 
      onClose={() => {
        onClose();
        setLocalSampleGroups({});
        setLocalColors({});
        setGroupOrder([]);
        setExpandedGroups(new Set());
        setEditingGroupName(null);
        setEditingValue('');
        setUnassignedSamples([]);
      }} 
      fullWidth 
      maxWidth="md"
    >
      <DialogTitle>Sample Groups</DialogTitle>
      <DialogContent>
        <List sx={{ width: '100%', bgcolor: 'background.paper', mb: 2 }}>
          {groupOrder.map((groupName) => (
            <Box 
              key={groupName} 
              sx={{ 
                mb: 1, 
                borderBottom: '1px solid',
                borderColor: 'grey.200',
              }}
            >
              <ListItem
                onClick={(e) => {
                  if (editingGroupName !== groupName) {
                    handleGroupToggle(groupName);
                  }
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: 1,
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                  '&:hover': {
                    bgcolor: 'grey.100',
                  },
                }}
              >
                <Box 
                  sx={{ 
                    flexGrow: 1, 
                    display: 'flex', 
                    alignItems: 'center',
                    position: 'relative', 
                  }}
                >
                  {editingGroupName === groupName ? (
                    <TextField
                      value={editingValue}
                      onChange={handleGroupNameChange}
                      onKeyPress={(e) => handleKeyPress(e, groupName)}
                      autoFocus
                      fullWidth
                      size="small"
                      onClick={(e) => e.stopPropagation()}
                      sx={{ mr: 2 }}
                    />
                  ) : (
                    <Box sx={{ flexGrow: 1, pr: 4, color: 'grey.800' }}>{groupName}</Box>
                  )}
                </Box>
                
                {editingGroupName === groupName && (
                  <Box
                    onClick={(e) => e.stopPropagation()}
                    sx={{ mr: 1, display: 'flex', alignItems: 'center' }}
                  >
                    <MuiColorInput
                      value={localColors[groupName] || DEFAULT_COLOR}
                      onChange={(color) => handleColorChange(groupName, color)}
                      format="hex"
                      isAlphaHidden
                      sx={{
                        width: '32px',
                        height: '32px',
                        padding: 0,
                        '& .MuiInputBase-root': {
                          width: '100%',
                          height: '100%',
                          padding: 0,
                        },
                        '& .MuiInputBase-input': { 
                          display: 'none' 
                        },
                        '& .MuiInputAdornment-root': { 
                          margin: 0,
                          height: '100%',
                        },
                        '& .MuiButtonBase-root': {
                          width: '100%',
                          height: '100%',
                          padding: 0,
                        },
                      }}
                    />
                  </Box>
                )}
                
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (editingGroupName === groupName) {
                      handleFinishEditing(groupName, editingValue);
                    } else {
                      handleStartEditing(groupName);
                    }
                  }}
                  sx={{ 
                    color: 'grey.600',
                    mr: 1,
                  }}
                >
                  {editingGroupName === groupName ? <Check fontSize="small" /> : <Edit fontSize="small" />}
                </IconButton>
                
                <IconButton 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleGroupDelete(groupName);
                  }}
                  sx={{ color: 'grey.600', mr: 1 }}
                >
                  <Delete fontSize="small" />
                </IconButton>
                
                <IconButton 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleGroupToggle(groupName);
                  }}
                  sx={{ 
                    transform: expandedGroups.has(groupName) ? 'rotate(180deg)' : 'none', 
                    transition: 'transform 0.3s',
                    color: 'grey.600',
                  }}
                >
                  {expandedGroups.has(groupName) ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </ListItem>
              
              <Collapse in={expandedGroups.has(groupName)} timeout="auto" unmountOnExit>
                <Box p={1} bgcolor="grey.50">
                  <SearchableDropdown
                    label="Samples"
                    value={localSampleGroups[groupName].map(id => ({
                      label: tumorDict[id],
                      id: id,
                      value: id.toString(),
                    }))}
                    colorDict={generateColorDict()}
                    setValue={(newValue: any) => handleSampleChange(groupName, newValue)}
                    options={getAvailableSamplesForGroup(groupName)}
                    placeholder="Select samples"
                    multiple={true}
                  />
                </Box>
              </Collapse>
            </Box>
          ))}
        </List>
        
        <Button
          variant="outlined"
          startIcon={<Add />}
          onClick={handleGroupAdd}
          sx={{ mb: 2, color: 'grey.700', borderColor: 'grey.300' }}
        >
          Add Sample Group
        </Button>
        
        {unassignedSamples.length > 0 && (
          <>
            <Box 
              sx={{ 
                width: '100%', 
                height: '1px', 
                bgcolor: 'grey.300', 
                my: 3 
              }} 
            />
            <Paper elevation={3} sx={{ p: 2, mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Unassigned Samples:
              </Typography>
              <Box 
                sx={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: 1, 
                  maxHeight: '150px', 
                  overflowY: 'auto',
                  p: 1,
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                }}
              >
                {unassignedSamples.map(sample => (
                  <Chip
                    key={sample.id}
                    label={sample.label}
                    size="small"
                  />
                ))}
              </Box>
            </Paper>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ color: 'grey.700', borderColor: 'grey.300' }}>Cancel</Button>
        <Button 
          onClick={() => onSave(localSampleGroups, localColors)} 
          color="primary"
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SampleGroupModal;